// The deals screen's changes view, apart from the screen so it can be tested.
//
// Two lists of `GET /api/reports/sales` say what happened over the period:
// the bridge's new and reopened deals (what came into the pipeline), and the
// stage moves (every deal whose stage at the period's end is not the one it
// had at its start, from Lead through Live). A deal whose estimate only got
// more accurate is in neither: that is not news. Nothing here makes money of
// its own beyond sums.

import type {
  DealState,
  SalesAmounts,
  SalesBridgeDeal,
  SalesReport,
  SalesStageMove,
} from "@crm/shared";
import { addDays, addMonths, calendarMonthOf, formatIsoMonth } from "../../format/calendarDate.js";
import { dealStateLabel } from "../../format/labels.js";
import {
  amountOf,
  thisMonth,
  type Measure,
  type Period,
  type PeriodShortcut,
} from "../sales/salesModel.js";

/**
 * Every stage a deal moves through, in order: a move to a later one is
 * forward. Lost is not on it: a lost deal is not drawn.
 */
export const STAGES: readonly DealState[] = [
  "lead",
  "qualified",
  "demoed",
  "proposal",
  "decision_maker_bought_in",
  "won",
  "implementation",
  "live",
  "ended",
];

/** How a deal is in the chart: it came into the pipeline, or moved stage. */
export type DealChange = "new" | "reopened" | "forward" | "back";

/** One deal as the chart draws it, from either of the report's two lists. */
export interface ChartDeal {
  readonly deal_id: string;
  readonly customer_name: string;
  readonly partner_name: string;
  readonly change: DealChange;
  readonly state_from: DealState | null;
  readonly state_to: DealState;
  readonly from: SalesAmounts | null;
  readonly to: SalesAmounts;
  readonly go_live_from: string | null;
  readonly go_live_to: string | null;
  readonly last_changed_by: SalesBridgeDeal["last_changed_by"];
  readonly last_changed_by_subject: string | null;
  readonly last_changed_at: string | null;
}

/** A deal that came into the open pipeline, or null for every other line of the bridge. */
export function cameIn(deal: SalesBridgeDeal): ChartDeal | null {
  if ((deal.category !== "new" && deal.category !== "reopened") || deal.state_to === null)
    return null;
  if (deal.to === null) return null;
  return { ...deal, change: deal.category, state_to: deal.state_to, to: deal.to };
}

/** A stage move between two stages of {@link STAGES}; a move to or from Lost is not drawn. */
export function moved(move: SalesStageMove): ChartDeal | null {
  const a = STAGES.indexOf(move.state_from);
  const b = STAGES.indexOf(move.state_to);
  if (a < 0 || b < 0 || a === b) return null;
  return { ...move, change: b > a ? "forward" : "back" };
}

/** How big a deal is drawn: what it is worth now, under the measure shown. */
export function dealSize(deal: ChartDeal, measure: Measure): number {
  return amountOf(deal.to, measure);
}

// ---------------------------------------------------------------- period

/** One-click periods for "what changed": rolling windows first, then calendar ones. */
export function changeShortcuts(today: string): readonly PeriodShortcut[] {
  const month = calendarMonthOf(today) ?? { year: 2026, month: 1 };
  const firstOf = formatIsoMonth;
  const lastMonth = addMonths(month, -1);
  const quarterStart = { year: month.year, month: month.month - ((month.month - 1) % 3) };
  return [
    { key: "7d", label: "Last 7 days", period: lastDays(today, 7) },
    { key: "30d", label: "Last 30 days", period: lastDays(today, 30) },
    { key: "90d", label: "Last 90 days", period: lastDays(today, 90) },
    { key: "this-month", label: "This month", period: thisMonth(today) },
    {
      key: "last-month",
      label: "Last month",
      period: {
        from: `${firstOf(lastMonth)}-01`,
        to: addDays(`${firstOf(month)}-01`, -1),
      },
    },
    {
      key: "this-quarter",
      label: "This quarter",
      period: { from: `${firstOf(quarterStart)}-01`, to: null },
    },
    { key: "this-year", label: "This year", period: { from: `${month.year}-01-01`, to: null } },
  ];
}

/** The `days` days up to and including today. */
export function lastDays(today: string, days: number): Period {
  return { from: addDays(today, -(days - 1)), to: null };
}

/** What the view opens on: a rolling month, so the 2nd of a month is not a nearly empty page. */
export function defaultChangesPeriod(today: string): Period {
  return lastDays(today, 30);
}

// ---------------------------------------------------------------- columns

/** The pipeline's stages; the rest of {@link STAGES} is signed. */
const PIPELINE: ReadonlySet<DealState> = new Set<DealState>([
  "lead",
  "qualified",
  "demoed",
  "proposal",
  "decision_maker_bought_in",
]);

/** Where a column sits in the funnel: the pipeline, or signed. */
export type ColumnGroup = "pipeline" | "signed";

export interface ChartColumn {
  readonly key: DealState;
  readonly group: ColumnGroup;
  /** Short enough for a column: "Bought-in", not "Decision Maker Bought-In". */
  readonly label: string;
  /** Moved forward into the stage. */
  readonly forward: readonly ChartDeal[];
  /** Came into the pipeline at this stage: new, or reopened. */
  readonly fresh: readonly ChartDeal[];
  /** Moved back into the stage. */
  readonly back: readonly ChartDeal[];
  /** Every deal in the column, under the measure shown. */
  readonly value: number;
}

export interface Chart {
  /** Every stage, from Lead to Ended. */
  readonly columns: readonly ChartColumn[];
  readonly opening: number;
  readonly closing: number;
  /** New and reopened deals: how many, and what they brought in. */
  readonly fresh: number;
  readonly cameIn: number;
  readonly forward: number;
  readonly back: number;
}

/** A stage's name in a column header; the full name stays on the deal itself. */
export function columnLabel(stage: DealState): string {
  return stage === "decision_maker_bought_in" ? "Bought-in" : dealStateLabel(stage);
}

/** Every deal a column holds, whatever brought it there. */
export function columnDeals(column: ChartColumn): readonly ChartDeal[] {
  return [...column.forward, ...column.fresh, ...column.back];
}

/**
 * The funnel, left to right: a column per stage from Lead to Ended, holding
 * every deal that arrived there in the period: moved forward, new or reopened
 * (in the stage it came in at), or moved back. Each column's value is exactly
 * the sum of the deals drawn in it.
 */
export function changeChart(report: SalesReport, measure: Measure): Chart {
  const sum = (deals: readonly ChartDeal[]) =>
    deals.reduce((total, deal) => total + dealSize(deal, measure), 0);
  // A deal created in the period has no stage at its start, so it is never
  // a stage move too; a reopened one moved from Lost, which is not drawn.
  const came = report.bridge.deals.flatMap((deal) => cameIn(deal) ?? []);
  const moves = report.stage_moves.flatMap((move) => moved(move) ?? []);
  const into = (stage: DealState, deals: readonly ChartDeal[]) =>
    deals.filter((deal) => deal.state_to === stage);
  // Every stage is always drawn, so the funnel's shape is the same from one
  // period to the next and an empty stage reads as nothing arriving there.
  const columns = STAGES.map((stage): ChartColumn => {
    const forward = into(stage, moves).filter((deal) => deal.change === "forward");
    const back = into(stage, moves).filter((deal) => deal.change === "back");
    const fresh = into(stage, came);
    return {
      key: stage,
      group: PIPELINE.has(stage) ? "pipeline" : "signed",
      label: columnLabel(stage),
      forward,
      fresh,
      back,
      value: sum(forward) + sum(fresh) + sum(back),
    };
  });
  return {
    columns,
    opening: amountOf(report.bridge.opening, measure),
    closing: amountOf(report.bridge.closing, measure),
    fresh: came.length,
    cameIn: sum(came),
    forward: moves.filter((deal) => deal.change === "forward").length,
    back: moves.filter((deal) => deal.change === "back").length,
  };
}

// ---------------------------------------------------------------- pieces

/** What a step's bar is stacked from: each deal, or each partner's deals together. */
export type Split = "deal" | "partner";

export const SPLITS: readonly Split[] = ["deal", "partner"];

/** Past this many partners the rest share one grey: a seventh hue would not be told apart. */
const COLOURED_PARTNERS = 6;

const SLOT_COLOURS = [
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
  "var(--series-5)",
  "var(--series-6)",
];

export const OTHER_PARTNERS = "__other__";

export interface PartnerSlot {
  /** The partner's name, or {@link OTHER_PARTNERS}. */
  readonly id: string;
  readonly name: string;
  readonly colour: string;
}

export interface PartnerSlots {
  /** Biggest mover first; "Other" last when anything folded. */
  readonly slots: readonly PartnerSlot[];
  readonly slotOf: (partnerName: string) => PartnerSlot;
}

/**
 * A colour per partner, by how much of the period's movement was theirs, so a
 * partner is the same colour in every column and under either split. The
 * report names a deal's partner, not its id, so the name is the key.
 */
export function partnerSlots(deals: readonly ChartDeal[], measure: Measure): PartnerSlots {
  const size = new Map<string, number>();
  for (const deal of deals) {
    const moved = dealSize(deal, measure);
    size.set(deal.partner_name, (size.get(deal.partner_name) ?? 0) + moved);
  }
  const ranked = [...size.entries()]
    .filter(([, moved]) => moved > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name]) => name);
  const coloured = ranked.slice(0, COLOURED_PARTNERS).map((name, index): PartnerSlot => ({
    id: name,
    name,
    colour: SLOT_COLOURS[index] ?? "var(--series-other)",
  }));
  const folded = ranked.length - coloured.length;
  const other: PartnerSlot = {
    id: OTHER_PARTNERS,
    name: folded === 1 ? (ranked[COLOURED_PARTNERS] ?? "Other") : "Other partners",
    colour: "var(--series-other)",
  };
  const byName = new Map(coloured.map((slot) => [slot.id, slot]));
  return {
    slots: folded > 0 ? [...coloured, other] : coloured,
    slotOf: (name) => byName.get(name) ?? other,
  };
}

export interface Piece {
  readonly key: string;
  /** A deal; a partner's deals together; or the deals too small to draw apart. */
  readonly kind: "deal" | "partner" | "rest";
  readonly label: string;
  /** The colour slot, or null for the rest, which may mix partners. */
  readonly slot: PartnerSlot | null;
  /** How many tiles tall it is drawn: one per deal, one for the rest. */
  readonly tiles: number;
  readonly deals: readonly ChartDeal[];
}

/**
 * The pieces one side of a column is stacked from, nearest the line first.
 * Every deal is one tile, so a column's height is how many deals it holds;
 * the biggest by value go nearest the line. Split by partner, a partner's
 * piece is as many tiles as it has deals there. Split by deal, a side with
 * more deals than `cap` tiles shows `cap - 1` and folds the rest into one.
 */
export function pieces(
  deals: readonly ChartDeal[],
  measure: Measure,
  split: Split,
  slots: PartnerSlots,
  cap: number,
): readonly Piece[] {
  const sorted = [...deals].sort(
    (a, b) =>
      dealSize(b, measure) - dealSize(a, measure) || a.customer_name.localeCompare(b.customer_name),
  );

  if (split === "partner") {
    return slots.slots.flatMap((slot): Piece[] => {
      const mine = sorted.filter((deal) => slots.slotOf(deal.partner_name).id === slot.id);
      if (mine.length === 0) return [];
      return [
        { key: slot.id, kind: "partner", label: slot.name, slot, tiles: mine.length, deals: mine },
      ];
    });
  }

  const room = Math.max(1, Math.floor(cap));
  const shown = sorted.length > room ? sorted.slice(0, room - 1) : sorted;
  const rest = sorted.slice(shown.length);
  const tiles: Piece[] = shown.map((deal) => ({
    key: deal.deal_id,
    kind: "deal",
    label: deal.customer_name,
    slot: slots.slotOf(deal.partner_name),
    tiles: 1,
    deals: [deal],
  }));
  if (rest.length > 0) {
    tiles.push({
      key: "rest",
      kind: "rest",
      label: `${rest.length} more`,
      slot: null,
      tiles: 1,
      deals: rest,
    });
  }
  return tiles;
}

export interface GoLiveShift {
  readonly from: string | null;
  readonly to: string;
  /** Later than before: the deal slipped. */
  readonly later: boolean;
}

/** A go-live that moved to another month in the period, or was set in it; null otherwise. */
export function goLiveShift(deal: ChartDeal): GoLiveShift | null {
  const to = deal.go_live_to?.slice(0, 7) ?? null;
  const from = deal.go_live_from?.slice(0, 7) ?? null;
  if (to === null) return null;
  if (deal.change === "new") return null;
  if (from === to) return null;
  return { from, to, later: from !== null && to > from };
}
